/*
 * pitch-detect.js — Timbrica shared LIVE fundamental-frequency detector.
 *
 * McLeod Pitch Method (MPM): the Normalised Square-Difference Function (NSDF)
 * with first-key-peak picking. Chosen after an empirical bake-off on REAL voice
 * recordings against librosa.pyin (the gold-standard probabilistic-YIN
 * reference): MPM beat both the legacy CMND-YIN (public/js/pitch-tracker.js,
 * used offline) and Chris-Wilson autocorrelation (guitar-tuner) on real low
 * male voice — recall 87% vs 44%/81%, agreement-within-50-cents 81% vs 40%/70%,
 * octave-error 4% vs 0%/10%. See tests/scratch/vrange-lab + the committed test
 * tests/js/pitch-detect.test.mjs.
 *
 * This is the SINGLE source of truth for real-time frame pitch detection. It is
 * a pure, dependency-free, frame-in → {freq,clarity}-out function so any tool
 * (vocal-range today; guitar-tuner / shadowing-studio are candidates) can share
 * it. Offline whole-buffer analysis still lives in pitch-tracker.js.
 *
 * Usage (browser):
 *   const r = PitchDetect.detect(float32Frame, audioCtx.sampleRate);
 *   if (r) { r.freq; r.clarity; }   // clarity ∈ [0,1] = NSDF peak height
 *
 * Key design notes (each validated, not assumed):
 *  - 2048-sample window is the sweet spot. A larger window REGRESSED on the
 *    aperiodic vocal-fry at the very bottom of a singer's range.
 *  - All lag bounds derive from the live sampleRate (never literal sample
 *    counts) so 44.1/48/16 kHz all work.
 *  - The lag-search floor sits ABOVE the vocal ceiling (searchFmax, default
 *    2400 Hz) so the highest note's first NSDF hump — whose rising zero-cross
 *    is at ~0.75·period — is captured. Otherwise top notes octave-DOWN
 *    (verified: F6 fails at a 1500 Hz floor, OK at 2400).
 *  - first-key-peak (first local max ≥ cutoff·globalMax), NOT the global max, is
 *    the octave/sub-harmonic guard that makes MPM beat plain autocorrelation.
 *    The cutoff is a trade, not a constant of nature:
 *      · a SUNG voice wants it high. Replayed on 233 real recordings (VocalSet,
 *        20 singers, four simulated microphones) at 0.8, 6 % of male frames
 *        read an octave UP — a low voice through a mic that rolls off the
 *        fundamental is nearly periodic at half its period, and 0.8 accepts
 *        that half. At 0.93: about 1 %; captured notes an octave off
 *        2.9 % → 0.2 %.
 *      · a MIXTURE wants it low. With music or a second voice behind the
 *        singer the global maximum can sit at a long lag that belongs to
 *        nobody, and a high cutoff walks down to it. Same singers over a TV
 *        10 dB quieter than them: the background took the lowest note in
 *        8–12 % of runs at 0.8 and 18–21 % at 0.93.
 *    The two errors look different from inside: when 0.8 and 0.93 disagree on
 *    clean singing, 0.93 is right 97 % of the time and its peak is almost
 *    perfectly periodic (median NSDF 0.99); in a mixture its peak is not
 *    (0.87). So `opts.strict = {cutoff, clarity}` takes the stricter pick
 *    ONLY when it reaches `clarity`. With {0.93, 0.95} it keeps the clean-
 *    singing gain (octave-off captures 0.2 %) and gives back most of the
 *    mixture loss (10–15 %). /vocal-range uses it; callers that pass nothing
 *    (the electric-guitar string tracker) get exactly the old behaviour.
 *    Measured and rejected: a flat 0.96; picking the 2×/3× lag whenever it is
 *    more periodic; switching to the strict pick only at integer lag ratios —
 *    each lost on one side or both.
 */
(function (root) {
    'use strict';

    // fmin is the lag-search FLOOR, not the lowest detectable note: maxTau =
    // floor(sampleRate/fmin) must EXCEED the target period, so fmin sits a hair
    // below the lowest note. 50 (~G#1) genuinely covers A1=55Hz; 55 did not
    // (floor(48000/55)=872 < A1's 872.7-sample period → A1 read NULL).
    var DEFAULTS = { fmin: 50, searchFmax: 2400, minClarity: 0.5, cutoff: 0.8 };

    function rms(buf) {
        var s = 0;
        for (var i = 0; i < buf.length; i++) s += buf[i] * buf[i];
        return Math.sqrt(s / buf.length);
    }

    // Fraction of railed samples — flags clipping (e.g. AGC-off belted notes
    // slamming the ADC, which is rich in odd harmonics).
    function clipFraction(buf) {
        var c = 0;
        for (var i = 0; i < buf.length; i++) if (Math.abs(buf[i]) >= 0.98) c++;
        return c / buf.length;
    }

    function detect(buf, sampleRate, opts) {
        opts = opts || {};
        var fmin = opts.fmin || DEFAULTS.fmin;
        var searchFmax = opts.searchFmax || DEFAULTS.searchFmax;
        var minClarity = opts.minClarity != null ? opts.minClarity : DEFAULTS.minClarity;
        var cutoffK = opts.cutoff != null ? opts.cutoff : DEFAULTS.cutoff;
        var strict = opts.strict && opts.strict.cutoff > 0 ? opts.strict : null;

        var n = buf.length;
        var maxTau = Math.min(Math.floor(sampleRate / fmin), n - 1);
        var minTau = Math.max(2, Math.floor(sampleRate / searchFmax));
        if (maxTau <= minTau) return null;

        // NSDF: n(tau) = 2·ACF(tau) / Σ(x[j]² + x[j+tau]²). The energy term in
        // the denominator makes it robust to amplitude envelope and to spectra
        // where the fundamental is weaker than its harmonics (low male voice).
        var nsdf = new Float32Array(maxTau + 1);
        for (var tau = minTau; tau <= maxTau; tau++) {
            var acf = 0, m = 0, lim = n - tau;
            for (var j = 0; j < lim; j++) {
                var a = buf[j], b = buf[j + tau];
                acf += a * b;
                m += a * a + b * b;
            }
            nsdf[tau] = m > 0 ? (2 * acf) / m : 0;
        }

        // Key maxima: local maxima bracketed by a positive- then negative-going
        // zero crossing. Pick the FIRST whose value ≥ cutoff·globalMax (octave guard).
        var globalMax = 0, pos = false, curMax = -1, curMaxPos = -1, peaks = [];
        for (var t = minTau + 1; t <= maxTau; t++) {
            if (nsdf[t] > 0 && nsdf[t - 1] <= 0) { pos = true; curMax = -1; curMaxPos = -1; }
            else if (nsdf[t] <= 0 && nsdf[t - 1] > 0) { if (curMaxPos > 0) peaks.push(curMaxPos); pos = false; }
            if (pos && nsdf[t] > curMax) { curMax = nsdf[t]; curMaxPos = t; if (curMax > globalMax) globalMax = curMax; }
        }
        if (curMaxPos > 0) peaks.push(curMaxPos);
        if (!peaks.length || globalMax <= 0) return null;

        var cutoff = cutoffK * globalMax, chosen = -1;
        for (var k = 0; k < peaks.length; k++) { if (nsdf[peaks[k]] >= cutoff) { chosen = peaks[k]; break; } }
        if (strict && chosen >= 0) {
            var sCut = strict.cutoff * globalMax;
            for (var s = 0; s < peaks.length; s++) {
                if (nsdf[peaks[s]] < sCut) continue;
                if (peaks[s] !== chosen && peaks[s] < maxTau && nsdf[peaks[s]] >= strict.clarity) chosen = peaks[s];
                break;
            }
        }
        if (chosen < 1 || chosen >= maxTau) return null;

        var clarity = Math.max(0, Math.min(1, nsdf[chosen]));
        if (clarity < minClarity) return null;

        // Parabolic interpolation on the chosen NSDF peak for sub-bin lag.
        var x1 = nsdf[chosen - 1], x2 = nsdf[chosen], x3 = nsdf[chosen + 1];
        var aa = (x1 + x3 - 2 * x2) / 2, bb = (x3 - x1) / 2, lag = chosen;
        if (aa !== 0) lag = chosen - bb / (2 * aa);
        if (lag < 1) return null;

        return { freq: sampleRate / lag, clarity: clarity };
    }

    var api = { detect: detect, rms: rms, clipFraction: clipFraction, DEFAULTS: DEFAULTS, VERSION: '1.1.0' };
    root.PitchDetect = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof self !== 'undefined' ? self : (typeof globalThis !== 'undefined' ? globalThis : this));
